import { MedplumClient } from "@medplum/core";
import {
  CodeableConcept,
  Encounter,
  Observation,
  Patient,
  Practitioner,
  Quantity,
  Reference,
} from "@medplum/fhirtypes";

import { formatISO } from "date-fns";
import isEmpty from "lodash/isEmpty";
import pluralize from "pluralize";

import { LaborStage, LaborStages } from "@/encounters/components/EncounterLabor/types/labor";
import { LOINCAnswerCodes, LOINCCodes } from "@/fhir/enums/loinc";
import {
  AdmissionDischargeComponent,
  BirthingComponent,
  CervicalExamComponent,
  ContractionsComponent,
  EstimatedBloodLossComponent,
  FetalHeartRateComponent,
  InputOutputComponent,
  LaborFlowsheetForms,
  LaborPositionComponent,
  MedsTherapiesComponent,
  MembranesComponent,
  QuiltedHealthCodes,
  ResuscitationComponent,
  ROMComponent,
  ShoulderDystociaComponent,
  TransportComponent,
} from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { joinArrayAsSentence } from "@/utils/array";
import {
  compareDateTimeStrings,
  getLocalDateString,
  localTimeFromISO,
  longUSDateTimeFormat,
} from "@/utils/datetime";
import {
  getCodeableConceptCode,
  getCodeableConceptCodingLabels,
  getCodeableConceptDisplay,
  getCodingDisplayLabel,
  getPractitionerName,
} from "@/utils/medplum";

export const TEMPERATURE_UNIT_DISPLAY = {
  "[degF]": "F",
  Cel: "C",
} as const;

export function getLaborStageObservation(
  status: Observation["status"],
  subject: Reference<Patient>,
  encounter: Reference<Encounter>,
  performer: Reference<Practitioner>,
  effectiveDateTime: Date,
  stage: LaborStage,
): Observation {
  return {
    resourceType: "Observation",
    status,
    code: {
      coding: [
        {
          system: Systems.LOINC,
          code: LOINCCodes.LaborStage,
          display: "Labor stage [Type]",
        },
      ],
    },
    subject,
    encounter,
    performer: [performer],
    effectiveDateTime: formatISO(effectiveDateTime),
    issued: formatISO(new Date()),
    valueCodeableConcept: {
      coding: [{ system: Systems.LOINC, code: stage, display: LaborStages[stage] }],
    },
  };
}

export function getFHRRowValues(observation: Observation | undefined): {
  fhr: string | undefined;
  measured: string | undefined;
  listeningDevice: string | undefined;
  accelerationsDetected: boolean | undefined;
  decelerationsDetected: boolean | undefined;
  baseline: boolean | undefined;
  baselineFHR: string | undefined;
  baselineDuration: string | undefined;
  relationToContractions: string | undefined;
  durationOfListening: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
} {
  const relationToContractions = getCodeableConceptCodingLabels(
    getComponentValueCodeableConcept(observation, FetalHeartRateComponent.RelationToContractions),
  );

  const durationOfListening = getComponentValueQuantity(
    observation,
    FetalHeartRateComponent.DurationOfListening,
  )?.value?.toString();

  const answers = {
    fhr: getFHRDisplay(observation),
    measured: getFHRMeasuredDisplay(observation),
    listeningDevice: getFHRListeningDeviceDisplay(observation),
    accelerationsDetected: getFHRBooleanDisplay(
      observation,
      FetalHeartRateComponent.AccelerationsDetected,
    ),
    decelerationsDetected: getFHRBooleanDisplay(
      observation,
      FetalHeartRateComponent.DecelerationsDetected,
    ),
    baseline: getFHRBooleanDisplay(observation, FetalHeartRateComponent.AssessBaseline),
    baselineFHR: getFHRBaselineFHRDisplay(observation),
    baselineDuration: getFHRBaselineDurationDisplay(observation),
    relationToContractions,
    durationOfListening,
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["fhr"]);
  return { ...answers, hasAnswers };
}

export function getLaborVitalsRowValues(observation: Observation | undefined): {
  bloodPressure: string | undefined;
  pulse: string | undefined;
  temperature: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    bloodPressure: getLaborVitalsBloodPressureDisplay(observation),
    pulse: getLaborVitalsPulseDisplay(observation),
    temperature: getLaborVitalsTemperatureDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getContractionsRowValues(observation: Observation | undefined): {
  frequency: string | undefined;
  duration: string | undefined;
  strength: string | undefined;
  coping: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    frequency: getContractionsFrequencyDisplay(observation),
    duration: getContractionsDurationDisplay(observation),
    strength: getContractionsStrengthDisplay(observation),
    coping: getContractionsCopingDisplay(observation),
    notes: getNotesDisplay(observation),
  };

  const hasAnswers = hasRecordsAnswers(answers, ["notes", "coping"]);
  const hasNotes = !isEmpty(answers.notes) || !isEmpty(answers.coping);

  return { ...answers, hasAnswers, hasNotes };
}

export function getCervicalExamRowValues(observation: Observation | undefined): {
  dilation: string | undefined;
  effacement: string | undefined;
  station: string | undefined;
  consistency: string | undefined;
  location: string | undefined;
  activeLabor: string | undefined;
  fetalPresentation: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    dilation: getCervicalExamDilationDisplay(observation),
    effacement: getCervicalExamEffacementDisplay(observation),
    station: getCervicalExamStationDisplay(observation),
    consistency: getCervicalExamConsistencyDisplay(observation),
    location: getCervicalExamLocationDisplay(observation),
    activeLabor: getCervicalExamActiveLaborDisplay(observation),
    fetalPresentation: getCervicalExamFetalPresentationDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, [
    "notes",
    "activeLabor",
    "location",
    "consistency",
    "fetalPresentation",
  ]);
  const hasNotes =
    !isEmpty(answers.notes) ||
    !isEmpty(answers.activeLabor) ||
    !isEmpty(answers.location) ||
    !isEmpty(answers.consistency) ||
    !isEmpty(answers.fetalPresentation);
  return { ...answers, hasAnswers, hasNotes };
}

export function getROMRowValues(observation: Observation | undefined): {
  method: string | undefined;
  confirmedBy: string | undefined;
  fluidColor: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    method: getROMMethodDisplay(observation),
    confirmedBy: getROMConfirmedByDisplay(observation),
    fluidColor: getROMFluidColorDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getPositionRowValues(observation: Observation | undefined): {
  clientPosition: string | undefined;
  inWater: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    clientPosition: getPositionClientPositionDisplay(observation),
    inWater: getPositionInWaterDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getAssessmentNoteRowValues(observation: Observation | undefined): {
  notes: string | undefined;
  hasNotes: boolean;
} {
  const answers = {
    notes: getNotesDisplay(observation),
  };
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasNotes };
}

export function getBirthingRowValues(observation: Observation | undefined): {
  crownDelivered: string | undefined;
  headDelivered: string | undefined;
  bodyDelivered: string | undefined;
  type: string | undefined;
  position: string | undefined;
  inWater: string | undefined;
  fetalPresentation: string | undefined;
  deliveryMethod: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    crownDelivered: getBirthingCrownDeliveredDisplay(observation),
    headDelivered: getBirthingHeadDeliveredDisplay(observation),
    bodyDelivered: getBirthingBodyDeliveredDisplay(observation),
    type: getBirthingTypeDisplay(observation),
    position: getBirthingPositionDisplay(observation),
    inWater: getBirthingInWaterDisplay(observation),
    fetalPresentation: getBirthingFetalPresentationDisplay(observation),
    deliveryMethod: getBirthingDeliveryMethodDisplay(observation),
    notes: getNotesDisplay(observation),
  };

  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);

  return { ...answers, hasAnswers, hasNotes };
}

export function getInputOutputRowValues(observation: Observation | undefined): {
  input: string | undefined;
  output: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    input: getInputOutputInputDisplay(observation),
    output: getInputOutputOutputDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getMembranesRowValues(observation: Observation | undefined): {
  pooling: string | undefined;
  nitrazine: string | undefined;
  fernResults: string | undefined;
  fluidColor: string | undefined;
  fluidColorOther: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const answers = {
    pooling: getMembranesPoolingDisplay(observation),
    nitrazine: getMembranesNitrazineDisplay(observation),
    fernResults: getMembranesFernResultsDisplay(observation),
    fluidColor: getMembranesFluidColorDisplay(observation),
    fluidColorOther: getMembranesFluidColorOtherDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getMedsTherapiesRowValues(observation: Observation | undefined): {
  medications: Array<{
    medication: string | undefined;
    form: string | undefined;
    duration: string | undefined;
    xDays: string | undefined;
    dosage: string | undefined;
    frequency: string | undefined;
    prn: string | undefined;
    specialInstructions: string | undefined;
  }>;
  therapies: Array<{
    therapy: string | undefined;
    reason: string | undefined;
  }>;
  ivAccess: string | undefined;
  fluidVolume: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const medications = getMedsTherapiesMedications(observation);
  const therapies = getMedsTherapiesTherapies(observation);
  const ivAccess = getMedsTherapiesIVAccess(observation);
  const fluidVolume = getMedsTherapiesFluidVolume(observation);
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(
    medications.length > 0 || therapies.length > 0 || ivAccess || fluidVolume,
  );
  const hasNotes = !isEmpty(notes);

  return {
    medications,
    therapies,
    ivAccess,
    fluidVolume,
    notes,
    hasAnswers,
    hasNotes,
  };
}

export function getLocationRowValues(observation: Observation | undefined): {
  locationSite: string | undefined;
  locationSiteOther: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const locationSite = getComponentValueCodeableConcept(observation, LOINCCodes.LocationSite)
    ?.coding?.[0]?.display;
  const locationSiteOther = getComponentValueString(observation, LOINCCodes.LocationSiteOther);
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(locationSite || locationSiteOther);
  const hasNotes = !isEmpty(notes);

  return {
    locationSite,
    locationSiteOther,
    notes,
    hasAnswers,
    hasNotes,
  };
}

export function getShoulderDystociaRowValues(observation: Observation | undefined): {
  duration: string | undefined;
  maneuvers: Array<{
    maneuver: string | undefined;
    initiated: string | undefined;
  }>;
  birth: string | undefined;
  birthNotes: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const duration = getComponentValueTime(observation, ShoulderDystociaComponent.Duration);
  const maneuvers = getShoulderDystociaManeuvers(observation);
  const birth = getComponentValueDateTime(observation, ShoulderDystociaComponent.Birth);
  const birthNotes = getComponentValueString(observation, ShoulderDystociaComponent.BirthNotes);
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(
    duration || (maneuvers && maneuvers.length > 0) || birth || birthNotes,
  );
  const hasNotes = !isEmpty(notes);

  return {
    duration,
    maneuvers,
    birth,
    birthNotes,
    notes,
    hasAnswers,
    hasNotes,
  };
}

export function getAdmissionDischargeRowValues(observation: Observation | undefined): {
  type: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const type = getComponentValueCodeableConcept(observation, AdmissionDischargeComponent.Type)
    ?.coding?.[0]?.display;
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(type);
  const hasNotes = !isEmpty(notes);

  return { type, notes, hasAnswers, hasNotes };
}

export function getEstimatedBloodLossRowValues(observation: Observation | undefined): {
  estimated: string | undefined;
  quantitative: string | undefined;
  cumulative: string | undefined;
  capture: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
  notes: string | undefined;
} {
  const estimated = getComponentValueQuantity(
    observation,
    LOINCCodes.EstimatedBloodLoss,
  )?.value?.toString();
  const quantitative = getComponentValueQuantity(
    observation,
    LOINCCodes.QuantitativeBloodLoss,
  )?.value?.toString();
  const cumulative = getComponentValueQuantity(
    observation,
    LOINCCodes.CumulativeBloodLoss,
  )?.value?.toString();
  const capture = getComponentValueCodeableConcept(observation, EstimatedBloodLossComponent.Capture)
    ?.coding?.[0]?.display;
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(estimated || quantitative || cumulative || capture);
  const hasNotes = !isEmpty(notes);

  return { estimated, quantitative, cumulative, capture, notes, hasAnswers, hasNotes };
}

export function getCumulativeBloodLossRowValues(observation: Observation | undefined): {
  cumulative: string | undefined;
  laborStage: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
  notes: string | undefined;
} {
  // For cumulative blood loss, the main value is in valueQuantity, not in components
  const cumulative = observation?.valueQuantity?.value?.toString();
  const laborStage = getComponentValueCodeableConcept(observation, "57071-3")?.coding?.[0]?.display;
  const notes = getNotesDisplay(observation);

  const hasAnswers = Boolean(cumulative || laborStage);
  const hasNotes = !isEmpty(notes);

  return { cumulative, laborStage, notes, hasAnswers, hasNotes };
}

export async function getTransportRowValues(
  observation: Observation | undefined,
  medplum: MedplumClient,
): Promise<TransportValues> {
  const type = getTransportType(observation);
  const reason = getTransportReason(observation, type);

  const decidingProviderUID = getComponentValueString(
    observation,
    TransportComponent.DecidingProvider,
  );

  let decidingProvider: string | undefined;
  if (decidingProviderUID) {
    try {
      const [providerName] = await getPractitionerName(medplum, decidingProviderUID);
      decidingProvider = providerName;
    } catch (error) {
      // Log the error but continue with undefined provider
      console.error("Failed to fetch practitioner name:", error);
    }
  }

  // Get dispositions based on transport type
  let newbornDisposition: string[] | undefined;
  let newbornDispositionOther: string | undefined;
  let clientDisposition: string[] | undefined;
  let clientDispositionOther: string | undefined;

  if (type === "Postpartum") {
    const result = getDispositionValues(
      observation,
      TransportComponent.NewbornDisposition,
      TransportComponent.NewbornDispositionOther,
    );
    newbornDisposition = result.disposition;
    newbornDispositionOther = result.other;
  } else if (type === "Newborn") {
    const result = getDispositionValues(
      observation,
      TransportComponent.ClientDisposition,
      TransportComponent.ClientDispositionOther,
    );
    clientDisposition = result.disposition;
    clientDispositionOther = result.other;
  }
  // Intrapartum gets no dispositions

  const timestamps = getTransportTimestamps(observation);

  const values = {
    type,
    decidingProvider,
    contact: getComponentValueString(observation, TransportComponent.Contact),
    reason,
    newbornDisposition,
    newbornDispositionOther,
    clientDisposition,
    clientDispositionOther,
    transportingFrom: getComponentValueString(observation, TransportComponent.TransportingFrom),
    hospitalName: getComponentValueString(observation, TransportComponent.HospitalName),
    hospitalContact: getComponentValueString(observation, TransportComponent.HospitalContact),
    condition: getComponentValueCodeableConcept(observation, TransportComponent.Condition)
      ?.coding?.[0]?.display,
    ...timestamps,
    notes: getNotesDisplay(observation),
  };

  const hasAnswers = Object.entries(values).some(([key, value]) => {
    if (key === "reason" || key === "newbornDisposition" || key === "clientDisposition") {
      return Array.isArray(value) && value.length > 0;
    }
    return Boolean(value);
  });
  const hasNotes = !isEmpty(values.notes);

  return {
    ...values,
    hasAnswers,
    hasNotes,
  };
}

export function getResuscitationValues(observation: Observation | undefined): {
  procedures: Array<{ name: string; time: string }>;
  birth: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  if (!observation?.component) {
    return {
      procedures: [],
      birth: undefined,
      notes: undefined,
      hasAnswers: false,
      hasNotes: false,
    };
  }

  const procedures: Array<{ name: string; time: string }> = [];

  // Extract all resuscitation procedures
  observation.component.forEach((comp) => {
    const code = getCodeableConceptCode(comp.code);
    const display = comp.code?.coding?.[0]?.display;
    const dateTime = comp.valueDateTime;

    if (code && display && dateTime) {
      procedures.push({
        name: display,
        time: new Date(dateTime).toLocaleString(),
      });
    }
  });

  const birth = getComponentValueDateTime(observation, ResuscitationComponent.ResuscitationBirth);
  const notes = getNotesDisplay(observation);
  const hasAnswers = procedures.length > 0 || Boolean(birth);
  const hasNotes = !isEmpty(notes);

  return {
    procedures,
    birth,
    notes,
    hasAnswers,
    hasNotes,
  };
}

function hasRecordsAnswers(
  answers: Record<string, string | number | boolean | undefined>,
  excludeKeys: string[],
): boolean {
  return Object.keys(answers).some((key) => {
    const value = answers[key as keyof typeof answers];
    return value !== undefined && value !== "" && !excludeKeys.includes(key);
  });
}

function getObservationComponentValue<T>(
  observation: Observation | undefined,
  codeMatcher: (code: string) => boolean,
  valueExtractor: (component: ObservationComponent) => T | undefined,
): T | undefined {
  const component = observation?.component?.find((comp) => {
    const code = getCodeableConceptCode(comp.code);
    return code && codeMatcher(code);
  });
  return component ? valueExtractor(component) : undefined;
}

function getComponentValueQuantity(
  observation: Observation | undefined,
  code: string,
): Quantity | undefined {
  return getObservationComponentValue(
    observation,
    (c) => c === code,
    (comp) => comp.valueQuantity,
  );
}

function getComponentValueCodeableConcept(
  observation: Observation | undefined,
  code: string,
): CodeableConcept | undefined {
  return getObservationComponentValue(
    observation,
    (c) => c === code,
    (comp) => comp.valueCodeableConcept,
  );
}

function getComponentValueString(
  observation: Observation | undefined,
  code: string,
): string | undefined {
  return getObservationComponentValue(
    observation,
    (c) => c === code,
    (comp) => comp.valueString,
  );
}

function getComponentValueDateTimeRaw(
  observation: Observation | undefined,
  code: string,
): string | undefined {
  const value = getObservationComponentValue(
    observation,
    (c) => c === code,
    (comp) => comp.valueDateTime,
  );
  return value;
}

function getComponentValueDateTime(
  observation: Observation | undefined,
  code: string,
): string | undefined {
  const value = getComponentValueDateTimeRaw(observation, code);
  return value ? longUSDateTimeFormat(value) : undefined;
}

function getComponentValueTime(
  observation: Observation | undefined,
  code: string,
): string | undefined {
  return getObservationComponentValue(
    observation,
    (c) => c === code,
    (comp) => comp.valueTime,
  );
}

function getNotesDisplay(observation: Observation | undefined): string | undefined {
  return observation?.note?.[0]?.text;
}

/* FHR Helpers */

function getFHRDisplay(observation: Observation | undefined): string {
  return [
    getComponentValueQuantity(
      observation,
      FetalHeartRateComponent.BeatsPerMinuteLow,
    )?.value?.toString(),
    getComponentValueQuantity(
      observation,
      FetalHeartRateComponent.BeatsPerMinuteHigh,
    )?.value?.toString(),
  ]
    .filter(Boolean)
    .join("-");
}

function getFHRMeasuredDisplay(observation: Observation | undefined): string | undefined {
  const contractions =
    getComponentValueCodeableConcept(observation, FetalHeartRateComponent.RelationToContractions)
      ?.coding?.map((coding) => getCodingDisplayLabel(coding).toLowerCase())
      .filter((label) => label.length > 0) ?? [];

  const relationToContractions = joinArrayAsSentence(contractions);

  const durationOfListening = getComponentValueQuantity(
    observation,
    FetalHeartRateComponent.DurationOfListening,
  )?.value;

  const durationOfListeningDisplay = pluralize("second", durationOfListening);

  if (durationOfListening && contractions.length > 0) {
    return `${durationOfListening} ${durationOfListeningDisplay} ${relationToContractions} contractions`;
  }

  if (durationOfListening) {
    return `${durationOfListening} ${durationOfListeningDisplay}`;
  }

  if (contractions.length > 0) {
    return `${relationToContractions} contractions`;
  }

  return;
}

function getFHRListeningDeviceDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, FetalHeartRateComponent.ListeningDevice)
    ?.coding?.[0]?.display;
}

function getFHRBooleanDisplay(
  observation: Observation | undefined,
  code: FetalHeartRateComponent,
): boolean | undefined {
  const answer = getComponentValueCodeableConcept(observation, code)?.coding?.[0]?.code;

  if (!answer) return;

  return answer === LOINCAnswerCodes.Yes;
}

function getFHRBaselineFHRDisplay(observation: Observation | undefined): string | undefined {
  const answer = getComponentValueQuantity(observation, FetalHeartRateComponent.Baseline)?.value;

  if (!answer) return;

  return `${answer} bpm`;
}

function getFHRBaselineDurationDisplay(observation: Observation | undefined): string | undefined {
  const answer = getComponentValueQuantity(
    observation,
    FetalHeartRateComponent.BaselineDurationOfListening,
  )?.value;

  if (!answer) return;

  return `${answer} seconds`;
}

/* Labor Vitals Helpers */

function getLaborVitalsBloodPressureDisplay(
  observation: Observation | undefined,
): string | undefined {
  const systolic = getComponentValueQuantity(observation, LOINCCodes.SystolicBloodPressure)?.value;
  const diastolic = getComponentValueQuantity(
    observation,
    LOINCCodes.DiastolicBloodPressure,
  )?.value;

  if (!systolic && !diastolic) return;
  return `${systolic}-${diastolic}`;
}

function getLaborVitalsTemperatureDisplay(
  observation: Observation | undefined,
): string | undefined {
  const temperatureComponent = getComponentValueQuantity(observation, LOINCCodes.BodyTemperature);

  if (!temperatureComponent) return;

  const temperature = temperatureComponent.value;
  if (temperature === undefined) return;

  const temperatureUnitCode = temperatureComponent.code ?? "[degF]";
  const temperatureUnit =
    TEMPERATURE_UNIT_DISPLAY[temperatureUnitCode as keyof typeof TEMPERATURE_UNIT_DISPLAY];

  const temperatureMethod = getCodeableConceptDisplay(observation?.method);

  if (!temperatureMethod) return `${temperature}°${temperatureUnit}`;

  return `${temperature}°${temperatureUnit} (${temperatureMethod})`;
}

function getLaborVitalsPulseDisplay(observation: Observation | undefined): string | undefined {
  const pulse = getComponentValueQuantity(observation, LOINCCodes.HeartRate)?.value;
  return pulse?.toString();
}

/* Contractions Helpers */

function getContractionsFrequencyDisplay(observation: Observation | undefined): string | undefined {
  const frequency = getComponentValueQuantity(observation, ContractionsComponent.Frequency)?.value;

  if (frequency === undefined) return;

  return `${frequency} min`;
}

function getContractionsDurationDisplay(observation: Observation | undefined): string | undefined {
  const duration = getComponentValueQuantity(observation, ContractionsComponent.Duration)?.value;

  if (duration === undefined) return;

  return `${duration} sec`;
}

function getContractionsStrengthDisplay(observation: Observation | undefined): string | undefined {
  const strengthCodeableConcept = getComponentValueCodeableConcept(
    observation,
    ContractionsComponent.Strength,
  );

  const strength = getCodeableConceptDisplay(strengthCodeableConcept);
  return strength;
}

function getContractionsCopingDisplay(observation: Observation | undefined): string | undefined {
  const copingCodeableConcept = getComponentValueCodeableConcept(
    observation,
    ContractionsComponent.Coping,
  );

  const coping = getCodeableConceptDisplay(copingCodeableConcept);
  return coping;
}

/* Cervical Exam Helpers */

function getCervicalExamDilationDisplay(observation: Observation | undefined): string | undefined {
  const dilationComponent = getComponentValueQuantity(observation, CervicalExamComponent.Dilation);

  if (!dilationComponent) return;

  const dilation = dilationComponent.value;
  if (dilation === undefined) return;
  const dilationUnit = dilationComponent.unit ?? "cm";

  return `${dilation} ${dilationUnit}`;
}

function getCervicalExamEffacementDisplay(
  observation: Observation | undefined,
): string | undefined {
  const effacementComponent = getComponentValueQuantity(
    observation,
    CervicalExamComponent.Effacement,
  );

  if (!effacementComponent) return;

  const effacement = effacementComponent.value;
  if (effacement === undefined) return;
  const effacementUnit = effacementComponent.unit ?? "%";

  return `${effacement}${effacementUnit}`;
}

function getCervicalExamStationDisplay(observation: Observation | undefined): string | undefined {
  const value = getComponentValueQuantity(observation, CervicalExamComponent.Station)?.value;
  if (value === undefined) return undefined;
  return value > 0 ? `+${value}` : value.toString();
}

function getCervicalExamConsistencyDisplay(
  observation: Observation | undefined,
): string | undefined {
  const consistencyCodeableConcept = getComponentValueCodeableConcept(
    observation,
    CervicalExamComponent.Consistency,
  );

  const consistency = getCodeableConceptDisplay(consistencyCodeableConcept);
  return consistency;
}

function getCervicalExamLocationDisplay(observation: Observation | undefined): string | undefined {
  const locationCodeableConcept = getComponentValueCodeableConcept(
    observation,
    CervicalExamComponent.Location,
  );

  const location = getCodeableConceptDisplay(locationCodeableConcept);
  return location;
}

function getCervicalExamActiveLaborDisplay(
  observation: Observation | undefined,
): string | undefined {
  const activeLaborCodeableConcept = getComponentValueCodeableConcept(
    observation,
    CervicalExamComponent.ActiveLabor,
  );

  const activeLabor = getCodeableConceptDisplay(activeLaborCodeableConcept);
  return activeLabor;
}

function getCervicalExamFetalPresentationDisplay(
  observation: Observation | undefined,
): string | undefined {
  const fetalPresentationCodeableConcept = getComponentValueCodeableConcept(
    observation,
    CervicalExamComponent.FetalPresentation,
  );

  const fetalPresentation = getCodeableConceptDisplay(fetalPresentationCodeableConcept);
  return fetalPresentation;
}

/* ROM Helpers */

function getROMMethodDisplay(observation: Observation | undefined): string | undefined {
  return getCodeableConceptDisplay(observation?.method);
}

function getROMConfirmedByDisplay(observation: Observation | undefined): string | undefined {
  const confirmedByCodeableConcept = getComponentValueCodeableConcept(
    observation,
    ROMComponent.ConfirmedBy,
  );
  if (!confirmedByCodeableConcept) return;

  if (confirmedByCodeableConcept.coding?.[0]?.code === QuiltedHealthCodes.ROMConfirmedByOther) {
    const confirmedByOther = getComponentValueString(observation, ROMComponent.ConfirmedByOther);
    return confirmedByOther;
  }

  return getCodeableConceptDisplay(confirmedByCodeableConcept);
}

function getROMFluidColorDisplay(observation: Observation | undefined): string | undefined {
  const fluidColorCodeableConcept = getComponentValueCodeableConcept(
    observation,
    LOINCAnswerCodes.AmnioticFluid,
  );
  if (!fluidColorCodeableConcept) return;

  if (fluidColorCodeableConcept.coding?.[0]?.code === QuiltedHealthCodes.ROMFluidColorOther) {
    const fluidColorOther = getComponentValueString(observation, ROMComponent.FluidColorOther);
    return fluidColorOther;
  }

  return getCodeableConceptDisplay(fluidColorCodeableConcept);
}

/* Position Helpers */

function getPositionClientPositionDisplay(
  observation: Observation | undefined,
): string | undefined {
  return getComponentValueCodeableConcept(observation, LaborPositionComponent.ClientPosition)
    ?.coding?.[0]?.display;
}

function getPositionInWaterDisplay(observation: Observation | undefined): string | undefined {
  const inWaterCodeableConcept = getComponentValueCodeableConcept(
    observation,
    LaborPositionComponent.ClientInWater,
  );

  if (!inWaterCodeableConcept) return;

  if (inWaterCodeableConcept.coding?.[0]?.code === QuiltedHealthCodes.ClientInWaterOther) {
    const inWaterOther = getComponentValueString(
      observation,
      LaborPositionComponent.ClientInWaterOther,
    );
    return inWaterOther;
  }

  return getCodeableConceptDisplay(inWaterCodeableConcept);
}

/* Birthing Helpers */

function getBirthingCrownDeliveredDisplay(
  observation: Observation | undefined,
): string | undefined {
  const value = getComponentValueDateTimeRaw(observation, BirthingComponent.CrownDeliveredDateTime);
  return value ? longUSDateTimeFormat(value) : undefined;
}

function getBirthingHeadDeliveredDisplay(observation: Observation | undefined): string | undefined {
  const value = getComponentValueDateTimeRaw(observation, BirthingComponent.HeadDeliveredDateTime);
  return value ? longUSDateTimeFormat(value) : undefined;
}

function getBirthingBodyDeliveredDisplay(observation: Observation | undefined): string | undefined {
  const value = getComponentValueDateTimeRaw(observation, BirthingComponent.BodyDeliveredDateTime);
  return value ? longUSDateTimeFormat(value) : undefined;
}

function getBirthingTypeDisplay(observation: Observation | undefined): string | undefined {
  return getCodeableConceptDisplay(
    getComponentValueCodeableConcept(observation, BirthingComponent.BirthingType),
  );
}

function getBirthingPositionDisplay(observation: Observation | undefined): string | undefined {
  return getCodeableConceptDisplay(
    getComponentValueCodeableConcept(observation, BirthingComponent.Position),
  );
}

function getBirthingFetalPresentationDisplay(
  observation: Observation | undefined,
): string | undefined {
  return getCodeableConceptDisplay(
    getComponentValueCodeableConcept(observation, BirthingComponent.FetalPresentation),
  );
}

function getBirthingInWaterDisplay(observation: Observation | undefined): string | undefined {
  const inWaterCodeableConcept = getComponentValueCodeableConcept(
    observation,
    BirthingComponent.ClientInWater,
  );
  if (!inWaterCodeableConcept) return;

  if (inWaterCodeableConcept.coding?.[0]?.code === BirthingComponent.ClientInWaterOther) {
    const inWaterOther = getComponentValueString(observation, BirthingComponent.ClientInWaterOther);
    return inWaterOther;
  }

  return getCodeableConceptDisplay(inWaterCodeableConcept);
}

function getBirthingDeliveryMethodDisplay(
  observation: Observation | undefined,
): string | undefined {
  const deliveryMethodCodeableConcept = observation?.method;
  if (!deliveryMethodCodeableConcept) return;

  if (deliveryMethodCodeableConcept.coding?.[0]?.code === BirthingComponent.DeliveryMethodOther) {
    const deliveryMethodOther = getComponentValueString(
      observation,
      BirthingComponent.DeliveryMethodOther,
    );
    return deliveryMethodOther;
  }

  return getCodeableConceptDisplay(deliveryMethodCodeableConcept);
}

/* Input/Output Helpers */

function getInputOutputInputDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, InputOutputComponent.Input);
}

function getInputOutputOutputDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, InputOutputComponent.Output);
}

/* Membranes Helpers */

function getMembranesPoolingDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, MembranesComponent.Pooling)?.coding?.[0]
    ?.display;
}

function getMembranesNitrazineDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, MembranesComponent.Nitrazine)?.coding?.[0]
    ?.display;
}

function getMembranesFernResultsDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, MembranesComponent.FernResults)?.coding?.[0]
    ?.display;
}

function getMembranesFluidColorDisplay(observation: Observation | undefined): string | undefined {
  const fluidColorComponent = observation?.component?.find(
    (component) => getCodeableConceptCode(component.code) === MembranesComponent.FluidColor,
  );

  if (!fluidColorComponent?.valueCodeableConcept) return undefined;

  // If the fluid color is "Other", we should show "Other" as the display
  if (
    fluidColorComponent.valueCodeableConcept.coding?.[0]?.code ===
    QuiltedHealthCodes.ROMFluidColorOther
  ) {
    return fluidColorComponent.valueCodeableConcept.coding?.[0]?.display;
  }

  return fluidColorComponent.valueCodeableConcept.coding?.[0]?.display;
}

function getMembranesFluidColorOtherDisplay(
  observation: Observation | undefined,
): string | undefined {
  return observation?.component?.find(
    (component) =>
      getCodeableConceptCode(component.code) === MembranesComponent.FluidColor &&
      component.valueString !== undefined,
  )?.valueString;
}

/* Meds & Therapies Helpers */

type ObservationComponent = NonNullable<Observation["component"]>[number];
type ObservationComponentArray = NonNullable<Observation["component"]>;

function updateRecord<T extends Record<string, string | undefined>>(
  records: T[],
  code: string,
  pattern: RegExp,
  getValue: (component: ObservationComponent) => string | undefined,
  component: ObservationComponent,
): { index: number; value: string | undefined } | null {
  const match = code.match(pattern);
  if (match) {
    const index = parseInt(match[1], 10) - 1;
    if (!records[index]) records[index] = {} as T;
    return { index, value: getValue(component) };
  }
  return null;
}

function getMedsTherapiesMedications(observation: Observation | undefined): Array<{
  medication: string | undefined;
  form: string | undefined;
  duration: string | undefined;
  xDays: string | undefined;
  dosage: string | undefined;
  frequency: string | undefined;
  prn: string | undefined;
  specialInstructions: string | undefined;
}> {
  const medications: Array<{
    medication: string | undefined;
    form: string | undefined;
    duration: string | undefined;
    xDays: string | undefined;
    dosage: string | undefined;
    frequency: string | undefined;
    prn: string | undefined;
    specialInstructions: string | undefined;
  }> = [];

  const components = observation?.component as ObservationComponentArray | undefined;
  components?.forEach((component) => {
    const code = getCodeableConceptCode(component.code);
    if (!code) return;

    const updates = [
      {
        pattern: /meds-therapies-medication-(\d+)/,
        key: "medication" as const,
        getValue: (c: ObservationComponent) =>
          c.valueCodeableConcept?.coding?.[0]?.display ||
          c.valueString ||
          "Medication not specified",
      },
      {
        pattern: /meds-therapies-form-(\d+)/,
        key: "form" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
      {
        pattern: /meds-therapies-duration-(\d+)/,
        key: "duration" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
      {
        pattern: /meds-therapies-duration-x-days-(\d+)/,
        key: "xDays" as const,
        getValue: (c: ObservationComponent) => c.valueQuantity?.value?.toString(),
      },
      {
        pattern: /meds-therapies-dosage-(\d+)/,
        key: "dosage" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
      {
        pattern: /meds-therapies-frequency-(\d+)/,
        key: "frequency" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
      {
        pattern: /meds-therapies-prn-(\d+)/,
        key: "prn" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
      {
        pattern: /meds-therapies-special-instructions-(\d+)/,
        key: "specialInstructions" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
    ];

    for (const { pattern, key, getValue } of updates) {
      const result = updateRecord(medications, code, pattern, getValue, component);
      if (result) {
        medications[result.index][key] = result.value;
        break;
      }
    }
  });

  return medications;
}

function getMedsTherapiesTherapies(observation: Observation | undefined): Array<{
  therapy: string | undefined;
  reason: string | undefined;
}> {
  const therapies: Array<{
    therapy: string | undefined;
    reason: string | undefined;
  }> = [];

  const components = observation?.component as ObservationComponentArray | undefined;
  components?.forEach((component) => {
    const code = getCodeableConceptCode(component.code);
    if (!code) return;

    const updates = [
      {
        pattern: /meds-therapies-therapy-(\d+)/,
        key: "therapy" as const,
        getValue: (c: ObservationComponent) => c.valueString || "Therapy not specified",
      },
      {
        pattern: /meds-therapies-reason-(\d+)/,
        key: "reason" as const,
        getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
      },
    ];

    for (const { pattern, key, getValue } of updates) {
      const result = updateRecord(therapies, code, pattern, getValue, component);
      if (result) {
        therapies[result.index][key] = result.value;
        break;
      }
    }
  });

  return therapies;
}

function getMedsTherapiesIVAccess(observation: Observation | undefined): string | undefined {
  if (!observation?.component) return undefined;
  const component = observation.component.find(
    (component) => getCodeableConceptCode(component.code) === MedsTherapiesComponent.IVAccess,
  );
  return component?.valueDateTime;
}

function getMedsTherapiesFluidVolume(observation: Observation | undefined): string | undefined {
  if (!observation?.component) return undefined;
  const component = observation.component.find(
    (component) => getCodeableConceptCode(component.code) === MedsTherapiesComponent.FluidVolume,
  );
  return component?.valueQuantity?.value?.toString();
}

/* Shoulder Dystocia Helpers */

function getShoulderDystociaManeuvers(
  observation: Observation | undefined,
): Array<{ maneuver: string | undefined; initiated: string | undefined }> {
  // Find all maneuver components (shoulder-dystocia-maneuvers-N)
  const maneuverComponents =
    observation?.component?.filter((comp) => {
      const code = getCodeableConceptCode(comp.code);
      return (
        code &&
        (code === ShoulderDystociaComponent.Maneuvers ||
          /^shoulder-dystocia-maneuvers-\d+$/.test(code))
      );
    }) || [];

  // Map maneuvers with their corresponding initiated timestamps
  const maneuvers = maneuverComponents.map((comp) => {
    const code = getCodeableConceptCode(comp.code);
    // Extract the index from the code (e.g., "shoulder-dystocia-maneuvers-1" -> "1")
    const indexMatch = code?.match(/^shoulder-dystocia-maneuvers-(\d+)$/);
    const index = indexMatch ? indexMatch[1] : null;

    // Find the corresponding initiated timestamp component
    let initiated: string | undefined;
    if (index) {
      const initiatedComponent = observation?.component?.find((c) => {
        const initiatedCode = getCodeableConceptCode(c.code);
        return initiatedCode === `${ShoulderDystociaComponent.ManeuverInitiated}-${index}`;
      });
      initiated = initiatedComponent?.valueDateTime;
    }

    return {
      maneuver: comp.valueCodeableConcept?.coding?.[0]?.display || "Maneuver not specified",
      initiated,
    };
  });

  return maneuvers;
}

/* Transport Helpers */
interface TransportValues {
  type: string | undefined;
  decidingProvider: string | undefined;
  contact: string | undefined;
  reason: string[] | undefined;
  newbornDisposition: string[] | undefined;
  newbornDispositionOther: string | undefined;
  clientDisposition: string[] | undefined;
  clientDispositionOther: string | undefined;
  transportingFrom: string | undefined;
  hospitalName: string | undefined;
  hospitalContact: string | undefined;
  condition: string | undefined;
  firstCall: string | undefined;
  emsArrived: string | undefined;
  timeLeft: string | undefined;
  arrivedAtHospital: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
}

function getTransportType(observation: Observation | undefined): string | undefined {
  const typeComponent = observation?.component?.find((comp) =>
    comp.code?.coding?.some((coding) => coding.code?.startsWith(TransportComponent.Type)),
  );
  return typeComponent?.valueCodeableConcept?.coding?.[0]?.display;
}

function getTransportReason(
  observation: Observation | undefined,
  type: string | undefined,
): string[] | undefined {
  if (!type) return undefined;

  const reasons: string[] = [];

  // Find all reason components that start with the transport reason prefix
  const reasonComponents = observation?.component?.filter((comp) =>
    comp.code?.coding?.[0]?.code?.startsWith(TransportComponent.Reason),
  );

  if (!reasonComponents || reasonComponents.length === 0) return undefined;

  reasonComponents.forEach((reasonComponent) => {
    const mainReason = reasonComponent.valueCodeableConcept?.coding?.[0]?.display;

    // If the reason is "Other", look for the specific other reason value
    if (mainReason === "Other") {
      // Try to find the other reason component with the specific type
      const otherReasonComponent = observation?.component?.find(
        (comp) => comp.code?.coding?.[0]?.code === `transportReason${type}Other`,
      );

      // If we found a component with a string value, use that
      if (otherReasonComponent?.valueString) {
        reasons.push(`Other (${otherReasonComponent.valueString})`);
        return;
      }

      // Otherwise, try to find any component with a string value that matches the pattern
      // but only if it's specifically an "Other" reason component
      const fallbackReasonComponent = observation?.component?.find(
        (comp) =>
          comp.code?.coding?.[0]?.code?.startsWith(`transportReason`) &&
          comp.code?.coding?.[0]?.code?.endsWith("Other") &&
          comp.valueString,
      );
      if (fallbackReasonComponent?.valueString) {
        reasons.push(`Other (${fallbackReasonComponent.valueString})`);
        return;
      }
    } else if (mainReason) {
      reasons.push(mainReason);
    }
  });

  return reasons.length > 0 ? reasons : undefined;
}

function getDispositionValues(
  observation: Observation | undefined,
  componentPrefix: string,
  otherComponent: string,
): { disposition: string[] | undefined; other: string | undefined } {
  const dispositionComponents = observation?.component?.filter((comp) =>
    comp.code?.coding?.some(
      (coding) =>
        coding.code === componentPrefix ||
        coding.code?.startsWith(componentPrefix.replace("/newborn", "")),
    ),
  );

  const dispositions: string[] = [];
  dispositionComponents?.forEach((component) => {
    const disposition = component.valueCodeableConcept?.coding?.[0]?.display;
    if (disposition) {
      dispositions.push(disposition);
    }
  });

  const other = getComponentValueString(observation, otherComponent);

  // If any disposition is "Other" and we have an other value, replace "Other" with "Other (otherValue)"
  if (dispositions.includes("Other") && other) {
    const otherIndex = dispositions.indexOf("Other");
    dispositions[otherIndex] = `Other (${other})`;
  }

  return {
    disposition: dispositions.length > 0 ? dispositions : undefined,
    other: undefined,
  };
}

function getTransportTimestamps(observation: Observation | undefined) {
  return {
    firstCall: getComponentValueDateTime(observation, TransportComponent.FirstCall),
    emsArrived: getComponentValueDateTime(observation, TransportComponent.EMSArrived),
    timeLeft: getComponentValueDateTime(observation, TransportComponent.TimeLeft),
    arrivedAtHospital: getComponentValueDateTime(observation, TransportComponent.ArrivedAtHospital),
  };
}

export function isBirthingObservation(observation: Observation): boolean {
  return (
    observation.status !== "cancelled" &&
    Boolean(
      observation.code?.coding?.some((coding) => coding.code === "forms/laborFlowSheet/birthing"),
    )
  );
}

export function shouldUpdateBirthObservation(
  observation: Observation,
  currentBirthDateTime?: string,
): boolean {
  if (!isBirthingObservation(observation)) return false;

  const bodyComponent = observation.component?.find((c) =>
    Boolean(c.code?.coding?.some((coding) => coding.code === "body-delivery-date-time")),
  );
  if (!bodyComponent) return false;

  const obsDateTime = bodyComponent.valueDateTime;
  return (
    !!obsDateTime &&
    (!currentBirthDateTime || new Date(obsDateTime) < new Date(currentBirthDateTime))
  );
}

export function isLaborObservationBeforeBirth(
  observation: Observation,
  birthDateTime?: string,
): boolean {
  if (!observation) return false;

  // Always show birthing observations - they are the source of birth time
  if (isBirthingObservation(observation)) return true;

  // Always show birthDateTime observations - they are the canonical birth time
  const code = getCodeableConceptCode(observation.code);
  if (code === QuiltedHealthCodes.BirthDateTime) return true;

  const observationsToFilter = [LaborFlowsheetForms.Resuscitation];
  if (!observationsToFilter.includes(code as LaborFlowsheetForms)) return true;
  if (!birthDateTime || !observation.effectiveDateTime) return true;
  // Use proper datetime comparison that handles both UTC and local datetime formats
  return !compareDateTimeStrings(observation.effectiveDateTime, birthDateTime);
}

export function filterRowsWithObservations<T extends { observations: Observation[] }>(
  rows: Record<string, T[]>,
): [string, T[]][] {
  return Object.entries(rows).filter(([_, groups]) =>
    groups.some((group) => group.observations && group.observations.length > 0),
  );
}

/**
 * Injects a birth time observation into rows data structure
 * Used by both labor and postpartum flowsheets to ensure proper ordering
 */
export function injectBirthTimeObservation<T extends { time: string; observations: Observation[] }>(
  rows: Record<string, T[]>,
  birthTimeObservation: Observation,
  birthDateTime: string,
): Record<string, T[]> {
  // Convert datetime to local date for proper grouping
  const date = getLocalDateString(birthDateTime);
  const time = localTimeFromISO(birthDateTime);

  if (!rows[date]) {
    rows[date] = [];
  }

  let timeRow = rows[date].find((row) => row.time === time);
  if (!timeRow) {
    timeRow = { time, observations: [] } as unknown as T;
    rows[date].push(timeRow);
  }

  // Add the birth time observation to the rows
  timeRow.observations.push(birthTimeObservation);

  // Re-sort the rows to maintain chronological order
  const sortedDates = Object.keys(rows).sort((a, b) => b.localeCompare(a));
  sortedDates.forEach((date) => {
    rows[date].sort((a, b) => b.time.localeCompare(a.time));
  });

  return rows;
}
